import { useCallback, useRef, useState } from "react";
import { useQuery } from "@apollo/client";

import { ACTIVITY_FEED } from "MVVM/GraphQL/homepage";
import { IActivityFeedNode } from "./@types";

// Smaller first page for a faster initial paint; later pages go bigger since
// useInfiniteScroll's early trigger means they load in the background well
// before the user reaches them.
const INITIAL_PAGE_SIZE = 12;
const NEXT_PAGE_SIZE = 25;

/* crypto.randomUUID is absent on some older Safari versions still in the
   wild — fall back to a timestamp-suffixed random id. */
const generateSeed = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  return `s_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

export const useActivityFeed = () => {
  /* Generated once per mount and reused for every subsequent page — a new
     seed reshuffles the whole feed from scratch, which is only intended for
     an explicit refresh (not implemented yet). */
  const seedRef = useRef<string>();
  if (!seedRef.current) seedRef.current = generateSeed();

  const { data, loading, error, fetchMore, refetch } = useQuery(ACTIVITY_FEED, {
    variables: { seed: seedRef.current, first: INITIAL_PAGE_SIZE },
    fetchPolicy: "cache-and-network",
    // Without this, `loading` only reflects the initial fetch — fetchMore()
    // wouldn't flip it back to true, so nothing gated useInfiniteScroll's
    // isLoading check against firing loadMore again while a page was still
    // in flight (more of a risk now that threshold triggers it earlier).
    notifyOnNetworkStatusChange: true,
  });

  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const pageInfo = data?.activityFeed?.pageInfo;

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;
    setLoadMoreError(null);

    // No cache typePolicy exists for `activityFeed` (see App.js), so a bare
    // fetchMore() would store the `after`-cursor page under a distinct cache
    // key instead of appending to it. updateQuery makes the merge explicit.
    fetchMore({
      variables: {
        seed: seedRef.current,
        first: NEXT_PAGE_SIZE,
        after: pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        return {
          activityFeed: {
            ...fetchMoreResult.activityFeed,
            nodes: [
              ...(previousResult.activityFeed?.nodes ?? []),
              ...(fetchMoreResult.activityFeed?.nodes ?? []),
            ],
          },
        };
      },
    }).catch(() => {
      setLoadMoreError("Couldn't load more.");
    });
  }, [fetchMore, pageInfo]);

  return {
    nodes: (data?.activityFeed?.nodes ?? []) as IActivityFeedNode[],
    loading,
    error,
    loadMoreError,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    loadMore,
    refetch,
  };
};
